import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useParams } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useEncryption } from "@/hooks/useEncryption";
import { Formik, Form, Field } from "formik";
import { type } from "arktype";
import { arktypeToFormikValidate } from "@/lib/validation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { ItemDetailPopover } from "@/components/shared/ItemDetailPopover";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ParentPicker } from "@/components/shared/ParentPicker";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { IconPicker } from "@/components/shared/IconPicker";
import { showToast } from "@/components/shared/Toast";
import { BottomDrawer } from "@/components/shared/BottomDrawer";
import { DropSpotIndicator } from "@/components/shared/DropSpotIndicator";
import { RelatedItemPicker } from "@/components/shared/RelatedItemPicker";
import { Plus, X, ChevronRight, ChevronDown, GripVertical, Copy } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useCollapsedState } from "@/hooks/useCollapsedState";
import {
  buildTree, flattenTree, getParentKeys,
  computeDropSpots, computeDropSpotUpdates,
  type FlattenedItem, type TreeItem, type DropSpot, type DepthOption,
} from "@/lib/tree-utils";
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors,
  useDraggable,
  type DragStartEvent, type DragEndEvent, type CollisionDetection,
} from "@dnd-kit/core";

/** Collision detection based on pointer position, not element center */
const pointerCollision: CollisionDetection = (args) => {
  const { pointerCoordinates } = args;
  if (pointerCoordinates) {
    return closestCenter({
      ...args,
      collisionRect: {
        width: 1, height: 1,
        top: pointerCoordinates.y, left: pointerCoordinates.x,
        right: pointerCoordinates.x + 1, bottom: pointerCoordinates.y + 1,
      },
    });
  }
  return closestCenter(args);
};

const rewardSchema = type({
  name: "string >= 1",
  icon: "string",
  description: "string",
  points: "number.integer >= 0",
});
const validateReward = arktypeToFormikValidate(rewardSchema);

interface Reward extends TreeItem {
  _key: string;
  name: string;
  icon: string;
  description: string;
  points: number;
  parent_key?: string | null;
  sort_order?: number;
  related_keys?: string[];
  included_keys?: string[];
  item_type?: "addon" | "scene" | null;
}

const RewardForm = memo(function RewardForm({
  editing, defaultParentKey, submitLoading, rewards, onSubmit, onClose,
}: {
  editing: Reward | null;
  defaultParentKey: string;
  submitLoading: boolean;
  rewards: Reward[] | null;
  onSubmit: (values: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  return (
    <Formik
      initialValues={{ name: editing?.name || "", icon: editing?.icon || "🎁", description: editing?.description || "", points: editing?.points || 0, parent_key: editing ? (editing.parent_key ?? "") : (defaultParentKey || ""), related_keys: editing?.related_keys || [], included_keys: editing?.included_keys || [], item_type: editing?.item_type || "" }}
      validate={validateReward}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="space-y-1">
              <Label>Icon</Label>
              <IconPicker value={values.icon} onSelect={(emoji) => setFieldValue("icon", emoji)} />
            </div>
            <div className="flex-1 space-y-1">
              <Label>Name</Label>
              <Field as={Input} name="name" placeholder="Reward name" autoFocus />
              {touched.name && errors.name && <p className="text-info text-app-danger">{errors.name}</p>}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <div className="flex gap-2">
              {[
                { value: "", label: "None", icon: "⊘" },
                { value: "scene", label: "Scene", icon: "🎬" },
                { value: "addon", label: "Addon", icon: "🧩" },
              ].map((opt) => {
                const active = (values.item_type || "") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFieldValue("item_type", opt.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      active
                        ? opt.value === "scene"
                          ? "bg-app-info/15 border-app-info/40 text-app-info"
                          : opt.value === "addon"
                            ? "bg-app-accent/15 border-app-accent/40 text-app-accent"
                            : "bg-app-surface-raised border-app-border text-app-text"
                        : "bg-transparent border-app-border/50 text-app-text-muted hover:border-app-border hover:bg-app-surface-raised/50"
                    }`}
                  >
                    <span>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Field as={Textarea} name="description" rows={values.item_type === "scene" ? 6 : undefined} className={values.item_type === "scene" ? "min-h-32" : undefined} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Cost (points)</Label>
              <Field as={Input} name="points" type="number" min={0} />
            </div>
            <div className="space-y-1">
              <Label>Parent Reward</Label>
              <ParentPicker
                items={rewards || []}
                value={values.parent_key || ""}
                onChange={(key) => setFieldValue("parent_key", key)}
                excludeKey={editing?._key}
                placeholder="None (top-level)"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Related Items</Label>
            <RelatedItemPicker
              items={rewards || []}
              selectedKeys={values.related_keys || []}
              onChange={(keys) => setFieldValue("related_keys", keys)}
              excludeKey={editing?._key}
              placeholder="Search related rewards..."
            />
          </div>
          <div className="space-y-1">
            <Label className="flex items-center gap-1.5 flex-wrap">
              Included Items
              {(() => {
                const total = (values.included_keys || []).reduce((sum, k) => {
                  const item = (rewards || []).find(i => i._key === k);
                  return sum + (item?.points || 0);
                }, 0);
                return total > 0 ? (
                  <span className="text-info font-normal">🎁 <span className="line-through text-app-text-muted">{Number(values.points) + total} pts</span> <span className="text-app-success font-medium">{values.points} pts</span></span>
                ) : (
                  <span className="text-info text-app-text-muted font-normal">(price reduced to 0)</span>
                );
              })()}
            </Label>
            <RelatedItemPicker
              items={rewards || []}
              selectedKeys={values.included_keys || []}
              onChange={(keys) => setFieldValue("included_keys", keys)}
              excludeKey={editing?._key}
              placeholder="Search included rewards..."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-app-primary hover:bg-app-primary-hover" disabled={submitLoading}>
              {submitLoading ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
});

const DraggableRewardItem = memo(function DraggableRewardItem({
  flatItem, onEdit, onDelete, onDuplicate, onAddChild, onToggleCollapse, isCollapsed, allItems, onJumpToItem,
}: {
  flatItem: FlattenedItem<Reward>;
  onEdit: (r: Reward) => void;
  onDelete: (key: string) => void;
  onDuplicate: (key: string) => void;
  onAddChild: (key: string) => void;
  onToggleCollapse: (key: string) => void;
  isCollapsed: boolean;
  allItems: Reward[];
  onJumpToItem: (key: string) => void;
}) {
  const r = flatItem.item;
  const depth = flatItem.depth;
  const {
    attributes, listeners, setNodeRef, isDragging,
  } = useDraggable({ id: r._key });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.25 : 1,
    marginLeft: depth > 0 ? `${Math.min(depth * 1.5, 6)}rem` : undefined,
  };

  const borderClass = r.item_type === "scene"
    ? (depth > 0 ? "border-l-app-info/40" : "border-l-app-info")
    : r.item_type === "addon"
      ? (depth > 0 ? "border-l-app-warning/40" : "border-l-app-warning")
      : (depth > 0 ? "border-l-app-accent/40" : "border-l-app-accent");

  const iconBg = r.item_type === "scene" ? "bg-app-info/10" : r.item_type === "addon" ? "bg-app-warning/10" : "bg-app-accent/10";

  return (
    <div ref={setNodeRef} style={style} {...attributes} data-item-key={r._key}>
      <Card className={`${depth > 0 ? "mt-1" : ""} border-l-4 ${borderClass} hover:shadow-md hover:bg-app-surface-raised/50 transition-all py-0 gap-0`}>
        <CardContent className="p-2 md:p-2.5">
          <div className="flex items-center gap-1.5">
            <button
              className="cursor-grab touch-none text-app-text-muted hover:text-app-text p-0.5 shrink-0"
              {...listeners}
              tabIndex={-1}
            >
              <GripVertical className="w-4 h-4" />
            </button>
            {flatItem.hasChildren ? (
              <button
                className="p-2 -m-1 shrink-0 text-app-text-muted hover:text-app-text rounded-md hover:bg-app-accent/10 active:bg-app-accent/20"
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(r._key); }}
              >
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <div className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer" onClick={() => onEdit(r)}>
              <span className={`${depth > 0 ? "w-7 h-7 text-base" : "w-8 h-8 text-lg"} rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>{r.icon}</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-body font-semibold">{r.name}</h3>
                <MarkdownText text={r.description} className="text-info text-app-text-muted" truncateAtSplit />
                <div className="flex gap-1.5 mt-1">
                  <span className="text-info bg-app-success/10 text-app-success px-2 py-0.5 rounded-full font-medium">{r.points} pts</span>
                  {r.item_type && (
                    <span className={`text-info px-2 py-0.5 rounded-full font-medium ${r.item_type === "scene" ? "bg-app-info/10 text-app-info" : "bg-app-warning/10 text-app-warning"}`}>
                      {r.item_type === "scene" ? "🎬 Scene" : "🧩 Addon"}
                    </span>
                  )}
                </div>
                {r.related_keys && r.related_keys.length > 0 && (() => {
                  const related = r.related_keys.map(k => allItems.find(i => i._key === k)).filter(Boolean) as Reward[];
                  return related.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {related.map(rel => (
                        <ItemDetailPopover key={rel._key} item={rel} pointsColor="success" pointsPrefix="-"
                          actions={[
                            { label: "Jump to Entry", onClick: () => onJumpToItem(rel._key) },
                            { label: "Edit", onClick: () => onEdit(rel) },
                          ]}>
                          <span className="text-[10px] leading-tight bg-app-surface-raised text-app-text-muted px-1.5 py-0.5 rounded-full border border-app-border/50 cursor-pointer hover:bg-app-surface-raised/80">
                            {rel.icon} {rel.name}
                          </span>
                        </ItemDetailPopover>
                      ))}
                    </div>
                  ) : null;
                })()}
                {r.included_keys && r.included_keys.length > 0 && (() => {
                  const included = r.included_keys.map(k => allItems.find(i => i._key === k)).filter(Boolean) as Reward[];
                  return included.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {included.map(rel => (
                        <ItemDetailPopover key={rel._key} item={rel} pointsColor="success" pointsPrefix="-"
                          actions={[
                            { label: "Jump to Entry", onClick: () => onJumpToItem(rel._key) },
                            { label: "Edit", onClick: () => onEdit(rel) },
                          ]}>
                          <span className="text-[10px] leading-tight bg-app-success/10 text-app-success px-1.5 py-0.5 rounded-full border border-app-success/20 cursor-pointer hover:bg-app-success/15">
                            🎁 {rel.icon} {rel.name}
                          </span>
                        </ItemDetailPopover>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="shrink-0" onClick={(e) => { e.stopPropagation(); onDuplicate(r._key); }}><Copy className="w-3.5 h-3.5 text-app-text-muted" /></Button>
            <Button variant="ghost" size="icon" className="shrink-0" onClick={(e) => { e.stopPropagation(); onAddChild(r._key); }}><Plus className="w-3.5 h-3.5 text-app-text-muted" /></Button>
            <Button variant="ghost" size="icon" className="shrink-0" onClick={(e) => { e.stopPropagation(); onDelete(r._key); }}><X className="w-4 h-4 text-app-danger" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export function AdminRewards() {
  const { jwt, clientId } = useParams();
  const { getHeaders } = useAuth();
  const { encryptFields, decryptList, hasKey } = useEncryption(clientId);
  const { data: rawRewards, loading, error, refetch } = useApi<Reward[]>(
    `/api/admin/${jwt}/${clientId}/rewards`,
  );
  const [rewards, setRewards] = useState<Reward[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Reward | null>(null);
  const [defaultParentKey, setDefaultParentKey] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  // Collapse state persisted in localStorage
  const { collapsedKeys, toggleCollapsed, initDefaults } = useCollapsedState("rewards-collapsed");

  // DnD state
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Decrypt reward names/descriptions after fetch
  useEffect(() => {
    if (!rawRewards) return;
    decryptList(rawRewards as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setRewards(d as unknown as Reward[]));
  }, [rawRewards, decryptList]);

  // Init default collapse state: collapse all parents on first load
  useEffect(() => {
    if (rewards) initDefaults(getParentKeys(rewards));
  }, [rewards, initDefaults]);

  // Computed tree + flat items (memoized to avoid recomputing on form keystroke)
  const tree = useMemo(() => rewards ? buildTree(rewards) : [], [rewards]);
  const flatItems = useMemo(() => flattenTree(tree, collapsedKeys, activeId), [tree, collapsedKeys, activeId]);

  // Drop spots (only computed during drag)
  const { visibleItems: dragVisibleItems, spotsByGap } = useMemo(() => {
    if (!activeId || !rewards) return { visibleItems: flatItems, spotsByGap: new Map<number, DropSpot>() };
    return computeDropSpots(flatItems, activeId, rewards);
  }, [flatItems, activeId, rewards]);

  const activeItem = activeId ? rewards?.find(r => r._key === activeId) : null;

  const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
    const encrypted = await encryptFields(values as Record<string, unknown>, ["name", "description"]);
    setSubmitLoading(true);
    try {
      const url = editing
        ? `/api/admin/${jwt}/${clientId}/rewards/${editing._key}`
        : `/api/admin/${jwt}/${clientId}/rewards`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(encrypted) });
      if (!res.ok) throw new Error((await res.json()).error);
      setShowForm(false);
      setEditing(null);
      refetch();
      showToast.success("Reward saved!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitLoading(false);
    }
  }, [editing, jwt, clientId, encryptFields, getHeaders, refetch]);

  const handleDelete = useCallback(async (key: string) => {
    const ok = await confirm({
      title: "Delete this reward?",
      description: "This reward will be permanently removed.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`/api/admin/${jwt}/${clientId}/rewards/${key}`, { method: "DELETE", headers: getHeaders() });
    refetch();
  }, [jwt, clientId, getHeaders, refetch, confirm]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    const draggedId = active.id as string;
    setActiveId(null);

    if (!over || !rewards) return;

    // Read resolved depth option from the droppable's data
    const data = over.data?.current as (DropSpot & DepthOption) | undefined;
    if (!data) return;

    const updates = computeDropSpotUpdates(
      rewards, draggedId, data.parentKey, data.sortOrder,
    );

    if (updates.length === 0) return;

    // Optimistic update
    const updatedRewards = rewards.map(r => {
      const upd = updates.find(u => u._key === r._key);
      if (upd) return { ...r, parent_key: upd.parent_key, sort_order: upd.sort_order };
      return r;
    });
    setRewards(updatedRewards);

    // If the item was reparented to a collapsed parent, expand it
    if (data.parentKey && collapsedKeys.has(data.parentKey)) {
      toggleCollapsed(data.parentKey);
    }

    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/rewards/reorder`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error("Reorder failed");
    } catch {
      refetch(); // Revert on error
      showToast.error("Failed to reorder");
    }
  }, [rewards, collapsedKeys, jwt, clientId, getHeaders, refetch, toggleCollapsed]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleDuplicate = useCallback(async (key: string) => {
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/rewards/${key}/duplicate`, {
        method: "POST",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      refetch();
      showToast.success("Reward duplicated!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to duplicate");
    }
  }, [jwt, clientId, getHeaders, refetch]);

  const handleEdit = useCallback((r: Reward) => { setEditing(r); setDefaultParentKey(""); setShowForm(true); }, []);
  const handleAddChild = useCallback((key: string) => { setEditing(null); setDefaultParentKey(key); setShowForm(true); }, []);
  const handleCloseForm = useCallback(() => { setShowForm(false); setEditing(null); }, []);

  const handleJumpToItem = useCallback((key: string) => {
    const el = document.querySelector(`[data-item-key="${key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-app-primary", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-app-primary", "rounded-xl"), 1500);
    }
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-heading font-bold">Rewards</h1>
        <Button onClick={() => { setShowForm(true); setEditing(null); setDefaultParentKey(""); }} className="bg-app-primary hover:bg-app-primary-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      <BottomDrawer open={showForm} onClose={handleCloseForm} title={editing ? "✏️ Edit Reward" : "✨ New Reward"}>
        {showForm && (
          <RewardForm
            editing={editing}
            defaultParentKey={defaultParentKey}
            submitLoading={submitLoading}
            rewards={rewards}
            onSubmit={handleSubmit}
            onClose={handleCloseForm}
          />
        )}
      </BottomDrawer>

      {!rewards || rewards.length === 0 ? (
        <EmptyState icon="🎁" title="No rewards yet" description="Create rewards for your partner to earn!" />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerCollision}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div>
            {(() => {
              const items = activeId ? dragVisibleItems : flatItems;
              return items.map((fi, idx) => (
                <div key={fi.item._key}>
                  {idx === 0 && activeId && spotsByGap.has(-1) && (
                    <DropSpotIndicator spot={spotsByGap.get(-1)!} variant="accent" />
                  )}
                  {idx > 0 && (
                    activeId && spotsByGap.has(idx - 1)
                      ? <DropSpotIndicator spot={spotsByGap.get(idx - 1)!} variant="accent" />
                      : <div className="h-2" />
                  )}
                  <DraggableRewardItem
                    flatItem={fi}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    onAddChild={handleAddChild}
                    onToggleCollapse={toggleCollapsed}
                    isCollapsed={collapsedKeys.has(fi.item._key)}
                    allItems={rewards!}
                    onJumpToItem={handleJumpToItem}
                  />
                  {idx === items.length - 1 && activeId && spotsByGap.has(idx) && (
                    <DropSpotIndicator spot={spotsByGap.get(idx)!} variant="accent" />
                  )}
                </div>
              ));
            })()}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeId && activeItem ? (
              <div className="opacity-40 shadow-lg rounded-lg">
                <Card className="border-l-4 border-l-app-accent py-0 gap-0">
                  <CardContent className="p-2 md:p-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="w-8 h-8 text-lg rounded-lg bg-app-accent/10 flex items-center justify-center">{activeItem.icon}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-body font-semibold">{activeItem.name}</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      {confirmDialog}
    </div>
  );
}
